import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CORE_DIVISIONS, IMAGES } from "@/data/content";

const ALL_SERVICES = [
  { title: "Cabinet Vision Library Development", body: "Creation, cleanup or restructuring of company cabinets, assemblies, construction methods, materials, hardware, attributes, parameters and templates.", value: "A stable foundation usable consistently across designers and engineers." },
  { title: "Parametric Cabinet Development", body: "Reusable cabinets and assemblies that respond intelligently to dimensions, construction, materials, hardware and selected conditions.", value: "Reduces repetitive modelling and makes complex standards easier to reuse." },
  { title: "UCS Programming", body: "Rule-based Cabinet Vision logic for parts, hardware, materials, machining, notes and conditional cabinet behaviour.", value: "Converts company knowledge into repeatable logic." },
  { title: "Approval & Production Drawings", body: "Customer-facing drawings and shop-floor documentation developed from coordinated project information.", value: "Less information loss between approval and production." },
  { title: "CNC & S2M Support", body: "Review of machining, construction and output conditions from Cabinet Vision into the manufacturing environment.", value: "Less scrap, fewer remakes and machine-side intervention." },
  { title: "Custom Report Development", body: "Reports designed around doors, hardware, parts, materials, purchasing, assembly, shipping and installation.", value: "Each department gets usable information automatically." },
  { title: "Hardware & Material Catalogues", body: "Organization of product codes, descriptions, selections and company-approved standards.", value: "Better consistency and ordering accuracy." },
  { title: "Workflow Analysis", body: "Review of information flow from sales through design, approvals, engineering, purchasing, CNC, assembly and installation.", value: "Identifies duplicate work and automation opportunities." },
  { title: "High-End Rendering", body: "Photorealistic interiors, custom cabinetry and architectural millwork presentations.", value: "Improves client understanding, approvals, portfolio quality and marketing." },
  { title: "Virtual Walkthrough & Animation", body: "Immersive and moving presentations of the space and cabinetry.", value: "Communicates circulation, scale and experience beyond static drawings." },
  { title: "Training & Documentation", body: "Company-specific training, SOPs, checklists, naming standards and references.", value: "Better adoption, consistency and long-term maintainability." },
  { title: "Ongoing Technical Support", body: "Continued development, troubleshooting, library management and workflow refinement.", value: "Keeps the system aligned with changing products and business needs." },
];

export default function Services() {
  return (
    <div data-testid="page-services">
      <PageHero
        overline="— Services"
        title="The full Designlayer"
        accent="service architecture."
        body="From early design concepts to CNC-ready production data — twelve service lines under one studio."
        image={IMAGES.cabinetry}
        testid="services-hero"
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16" data-testid="services-divisions">
        <div className="grid md:grid-cols-3 gap-6">
          {CORE_DIVISIONS.map((d) => (
            <Reveal key={d.title}>
              <Link to={d.href} className="dl-card block h-full p-8 group">
                <div className="overline mb-3">{d.overline}</div>
                <h3 className="font-serif text-2xl mb-3 text-[#EAEAEA] group-hover:text-[#B58B5D] transition-colors">{d.title}</h3>
                <p className="text-sm text-[#A3A3A3] leading-relaxed">{d.body}</p>
                <div className="mt-6 flex items-center gap-2 text-[#B58B5D] text-xs font-mono uppercase tracking-[0.18em]">
                  Service detail <ArrowRight size={14} />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <Reveal>
          <div className="overline mb-4">— Detailed services</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-14 max-w-3xl">
            Twelve service lines. One coordinated workflow.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-8">
          {ALL_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 30}>
              <div className="py-7 border-b border-white/5">
                <div className="flex items-baseline gap-5">
                  <span className="num text-2xl text-[#B58B5D]">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1">
                    <h3 className="font-serif text-xl text-[#EAEAEA] mb-2">{s.title}</h3>
                    <p className="text-[15px] text-[#A3A3A3] leading-relaxed">{s.body}</p>
                    <p className="text-sm text-[#B58B5D] mt-3 italic">Business value — {s.value}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
